import React, { Suspense, useRef, useEffect, useState, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { useGLTF } from "@react-three/drei";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

useGLTF.preload("/3dModel/injection1.glb");

function Model({ scrollProgress }) {
  const { scene } = useGLTF("/3dModel/injection1.glb");
  const clonedScene = useMemo(() => scene.clone(true), [scene]);
  const group = useRef();
  const [scaleFactor, setScaleFactor] = useState(1);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width <= 768) {
        setScaleFactor(0.45);
      } else {
        setScaleFactor(1);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (group.current) {
      group.current.scale.set(scaleFactor, scaleFactor, scaleFactor);
      group.current.position.set(0, 0, 0);
      group.current.rotation.set(-0.05, 0.2, 0.5);
    }
    return () => {
      clonedScene.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) obj.material.dispose();
      });
    };
  }, [clonedScene, scaleFactor]);

  useFrame((state) => {
    if (group.current) {
      const time = state.clock.getElapsedTime();
      group.current.rotation.y =
        scrollProgress.current * Math.PI * 2 + time * 0.2;
    }
  });

  return <primitive ref={group} object={clonedScene} />;
}

const ScrollRotateModel = ({ sectionRefs }) => {
  const containerRef = useRef();
  const canvasRef = useRef();
  const scrollProgress = useRef(0);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (containerRef.current) {
      setIsReady(true);
    }
  }, []);

  useEffect(() => {
    if (
      !sectionRefs.current.length ||
      !containerRef.current ||
      !canvasRef.current
    )
      return;

    const ctx = gsap.context(() => {
      const totalHeight = sectionRefs.current.reduce(
        (sum, el) => sum + (el ? el.getBoundingClientRect().height : 0),
        0
      );
      gsap.set(containerRef.current, { height: totalHeight });

      ScrollTrigger.create({
        trigger: containerRef.current,
        start: "top top",
        end: "bottom bottom",
        scrub: true,
        onUpdate: (self) => {
          scrollProgress.current = self.progress;
        },
      });
    });

    return () => ctx.revert();
  }, [isReady, sectionRefs]);

  useEffect(() => {
    const currentCanvas = canvasRef.current;
    return () => {
      if (currentCanvas) {
        currentCanvas
          .getContext("webgl")
          ?.getExtension("WEBGL_lose_context")
          ?.loseContext();
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="absolute inset-x-0 top-0 z-30"
      style={{
        pointerEvents: "none",
        overflow: "visible",
      }}
    >
      {isReady && (
        <Suspense fallback={null}>
          <Canvas
            ref={canvasRef}
            camera={{ position: [0, 0, 10], fov: 25 }}
            gl={{
              preserveDrawingBuffer: true,
              antialias: true,
              powerPreference: "low-power",
            }}
            style={{
              width: "100%",
              height: "100vh",
              position: "sticky",
              top: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 50,
              pointerEvents: "none",
            }}
          >
            <ambientLight intensity={0.5} />
            <directionalLight position={[10, 10, 5]} intensity={1} />
            <Model scrollProgress={scrollProgress} />
          </Canvas>
        </Suspense>
      )}
    </div>
  );
};

export default ScrollRotateModel;
